import React, { useMemo } from 'react';
import { I18n } from '@aws-amplify/core';

import { SearchIcon } from '@/assets/icons/v2';
import { useBeraReportV2 } from '@/context/BeraReportV2';

import { Card } from './Card';
import { useStretchFilters } from './_hooks';
import { FIRST_PAGE, REPORTS_PER_PAGE, STRETCH_FILTER, buildRangeLabel } from './utils';
import {
	CardsGrid,
	Container,
	EmptyMessage,
	FilterButton,
	FooterRow,
	HeaderRow,
	SearchInput,
	StretchPagination,
	Title
} from './styles';

import type { StretchFilter } from './utils';
import type { BeraReportV2 } from '@/types/Bera';

interface FilterReportsParams {
	reports: BeraReportV2[];
	search: string;
	filter: StretchFilter;
	outOfRangeReportIds: string[];
}

function filterReports({ reports, search, filter, outOfRangeReportIds }: FilterReportsParams): BeraReportV2[] {
	const term = search.trim().toLowerCase();

	const result = reports.filter((report) => {
		if (filter === STRETCH_FILTER.NOT_EVALUATED && report.is_evaluated) {
			return false;
		}

		if (filter === STRETCH_FILTER.OUT_OF_VIDEO && !outOfRangeReportIds.includes(report.id)) {
			return false;
		}

		if (!term) {
			return true;
		}

		return !!report.task_name?.toLowerCase().includes(term);
	});

	return result;
}

export function IdentifiedStretches(): JSX.Element {
	const {
		allReports,
		selectedReport,
		isEditMode,
		isVideoUnavailable,
		outOfRangeReportIds,
		onSelectReport,
		onHoverTask
	} = useBeraReportV2();

	const { search, page, filter, handleSearchChange, handlePageChange, handleFilterChange } = useStretchFilters();

	const filteredReports = useMemo(
		() => filterReports({ reports: allReports, search, filter, outOfRangeReportIds }),
		[allReports, search, filter, outOfRangeReportIds]
	);

	const pageStart = page * REPORTS_PER_PAGE;

	const rows = filteredReports.slice(pageStart, pageStart + REPORTS_PER_PAGE);

	const total = filteredReports.length;

	const hasAnyStretch = !!total;

	const hasFilter = !!search || filter !== STRETCH_FILTER.ALL;

	const hasOutOfRangeReport = !!outOfRangeReportIds.length;

	function handleSearchInputChange({ target }: React.ChangeEvent<HTMLInputElement>): void {
		handleSearchChange(target.value);
	}

	function handleShowAll(): void {
		handleFilterChange(STRETCH_FILTER.ALL);
	}

	function handleShowNotEvaluated(): void {
		handleFilterChange(STRETCH_FILTER.NOT_EVALUATED);
	}

	function handleShowOutOfVideo(): void {
		handleFilterChange(STRETCH_FILTER.OUT_OF_VIDEO);
	}

	if (!hasAnyStretch && !hasFilter) {
		return (
			<Container>
				<Title>{I18n.get('Identified stretches')}</Title>
				<EmptyMessage>
					{I18n.get('Select an interval of the video to create the first identified stretch.')}
				</EmptyMessage>
			</Container>
		);
	}

	return (
		<Container>
			<HeaderRow>
				<Title>{I18n.get('Identified stretches')}</Title>
				<SearchInput
					allowClear
					value={search}
					prefix={<SearchIcon />}
					placeholder={I18n.get('Search by TLID')}
					onChange={handleSearchInputChange}
				/>
				<FilterButton $isActive={filter === STRETCH_FILTER.ALL} onClick={handleShowAll}>
					{I18n.get('All')}
				</FilterButton>
				<FilterButton $isActive={filter === STRETCH_FILTER.NOT_EVALUATED} onClick={handleShowNotEvaluated}>
					{I18n.get('Unevaluated')}
				</FilterButton>
				{hasOutOfRangeReport && (
					<FilterButton $isActive={filter === STRETCH_FILTER.OUT_OF_VIDEO} onClick={handleShowOutOfVideo}>
						{I18n.get('Outside the video')}
					</FilterButton>
				)}
			</HeaderRow>
			{!hasAnyStretch && <EmptyMessage>{I18n.get('No TLID found for this filter')}</EmptyMessage>}
			{hasAnyStretch && (
				<CardsGrid>
					{rows.map((report) => (
						<Card
							key={report.id}
							report={report}
							isSelected={report.id === selectedReport?.id}
							outOfRange={outOfRangeReportIds.includes(report.id)}
							disabled={isEditMode}
							hideTimes={isVideoUnavailable}
							onSelect={onSelectReport}
							onHover={onHoverTask}
						/>
					))}
				</CardsGrid>
			)}
			{hasAnyStretch && (
				<FooterRow>
					<span>{buildRangeLabel(total, page)}</span>
					<StretchPagination
						showSizeChanger={false}
						hideOnSinglePage={false}
						current={page + FIRST_PAGE}
						pageSize={REPORTS_PER_PAGE}
						total={total}
						onChange={handlePageChange}
					/>
				</FooterRow>
			)}
		</Container>
	);
}
